import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The redirect must be derived from window.location.
const EMERGENT_AUTH_BASE = "https://auth.emergentagent.com/";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable button that launches Emergent-managed Google sign-in and calls
 * `onSuccess({ email, name, picture })` when the user returns and the session
 * has been exchanged.
 *
 * PREFILL ONLY — this component does NOT establish a persistent session for
 * the salon dashboard or the customer. Callers use the returned identity to
 * populate form fields.
 *
 * Props:
 *  - onSuccess: ({ email, name, picture }) => void
 *  - label:     optional button label override
 *  - lang:      "en" | "it" for translated labels (defaults en)
 *  - variant:   "light" (white bg) | "dark" (transparent bg) — visual style
 *  - testid:    data-testid override
 */
export default function GoogleAuthButton({
  onSuccess,
  label,
  lang = "en",
  variant = "light",
  testid = "google-auth-btn",
}) {
  const [busy, setBusy] = useState(false);
  const processed = useRef(false);

  // On mount, detect a #session_id=... fragment left over from the Emergent
  // Auth redirect. If found, exchange it for the user's profile and forward
  // to onSuccess. We use useRef to prevent StrictMode double-processing.
  useEffect(() => {
    if (processed.current) return;
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    processed.current = true;

    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    // Clear the fragment immediately so a refresh doesn't retrigger.
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch {}

    setBusy(true);
    axios
      .post(`${API}/auth/google/prefill`, { session_id: sessionId })
      .then((r) => {
        setBusy(false);
        const email = r?.data?.email || "";
        const name = r?.data?.name || "";
        const picture = r?.data?.picture || "";
        if (!email) {
          toast.error(lang === "it" ? "Impossibile recuperare l'account Google." : "Couldn't read your Google account.");
          return;
        }
        onSuccess?.({ email, name, picture });
      })
      .catch((e) => {
        setBusy(false);
        toast.error(
          e?.response?.data?.detail
            || (lang === "it" ? "Login Google non riuscito. Riprova." : "Google sign-in failed. Please try again."),
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const redirectUrl = window.location.origin + window.location.pathname + window.location.search;
    window.location.href = `${EMERGENT_AUTH_BASE}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const buttonLabel =
    label
    || (busy
      ? (lang === "it" ? "Recupero…" : "Fetching…")
      : (lang === "it" ? "Continua con Google" : "Continue with Google"));

  const isDark = variant === "dark";
  const className = isDark
    ? "w-full inline-flex items-center justify-center gap-2.5 rounded-full h-11 px-5 text-sm text-white bg-white/10 hover:bg-white/15 border border-white/25 backdrop-blur-sm transition-colors disabled:opacity-60"
    : "w-full inline-flex items-center justify-center gap-2.5 rounded-full h-11 px-5 text-sm text-[#383633] bg-white hover:bg-[#F8F6F2] border border-[#DDD6C7] transition-colors disabled:opacity-60";

  return (
    <button
      type="button"
      onClick={start}
      disabled={busy}
      data-testid={testid}
      className={className}
    >
      <GoogleG className="h-4 w-4" />
      <span className="font-medium">{buttonLabel}</span>
    </button>
  );
}

/** Google "G" logo — official brand colors, sized to match Lucide icons. */
function GoogleG({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}
